import { create } from 'zustand';
import { supabase } from '../lib/supabase';
import { syncManager } from '../lib/syncManager';
import { fetchUserPreferences } from '../lib/userPreferences';
import { createPreferenceSync } from '../lib/preferenceSync';
import { onSignOut } from '../lib/signOutReset';
import { useHabitStore } from './habitStore';
import { toLocalYMD, shiftYMD } from '../utils/hydration';
import { habitTarget, isLabeledHabit } from '../utils/habitProgress';
import {
  isDoneOn,
  linkedDoneDays,
  logKey,
  openLink,
  parsePractices,
  practiceLinkedTo,
  type CareLogRow,
  type CarePractice,
} from '../utils/carePractice';

/**
 * Care practices — Personal → Mind (scope 006, PR B). Rules: `utils/carePractice`.
 *
 * Definitions → `user_preferences.care_practices` (written whole).
 * Days       → `care_practice_log` (one row per practice per day).
 *
 * ⚠️⚠️ NOTHING IS WRITTEN BEFORE THE CLOUD COPY LOADS (`ready`). Definitions are
 * written whole, so a write against the empty default would erase the user's
 * practices. Same guard as `journalPromptStore`.
 *
 * ⚠️⚠️ A LINKED completion goes through the HABIT pipeline (`toggleHabit` /
 * `incrementHabit`) and is never mirrored here — see `utils/carePractice`. The
 * habit is the record; this store only reads it.
 *
 * ⚠️ Dates are LOCAL YMD everywhere (`toLocalYMD`). Parts of habitStore still use
 * UTC for "today" in streak maths; completion keys here never do.
 */

/** How far back the log is loaded — and therefore how far back care XP reaches. */
export const CARE_HISTORY_DAYS = 365;

export type LinkResult = 'ok' | 'not-ready' | 'no-practice' | 'no-habit' | 'unsupported' | 'habit-taken' | 'save-failed';

interface NewPractice { name: string; instructions?: string; starterKey?: string }

interface CarePracticeStore {
  practices: CarePractice[];
  /** Keyed by `logKey(practiceId, day)`. */
  log: Record<string, CareLogRow>;
  ready: boolean;
  loading: boolean;
  initializedUserId: string | null;
  /** The last save that failed, for the page to show. Cleared on the next success. */
  error: string | null;

  initialize: () => Promise<void>;
  addPractice: (p: NewPractice) => string | null;
  updatePractice: (id: string, patch: { name?: string; instructions?: string }) => void;
  archivePractice: (id: string) => void;
  restorePractice: (id: string) => void;
  linkHabit: (practiceId: string, habitId: string) => Promise<LinkResult>;
  unlinkHabit: (practiceId: string) => Promise<boolean>;
  complete: (practiceId: string, day: string) => Promise<void>;
  undo: (practiceId: string, day: string) => Promise<void>;
  setHelpful: (practiceId: string, day: string, helpful: boolean | null) => Promise<void>;
  /** Called by habitStore.deleteHabit BEFORE the habit is removed. */
  onHabitDeleted: (habitId: string) => Promise<void>;
  isDone: (practiceId: string, day: string) => boolean;
  clearError: () => void;
  clearStore: () => void;
}

const today = () => toLocalYMD(new Date());

/**
 * ⚠️⚠️ MOVED BY `clearStore` (sign-out). A load or a write notes it before its first
 * await and drops its answer when it has moved: work still in flight for the account
 * that signed out must not land in the next account's empty store.
 */
let epoch = 0;

interface LogRowDb { practice_id: string; day: string; done: boolean; helpful: boolean | null; habit_id: string | null }

const LOG_COLUMNS = 'practice_id, day, done, helpful, habit_id';
const fromDb = (r: LogRowDb): CareLogRow => ({
  practiceId: r.practice_id, day: r.day, done: r.done, helpful: r.helpful ?? null, habitId: r.habit_id ?? null,
});

/** Linking a habit whose completion is driven by something else would fight it. */
function linkable(habitId: string): 'ok' | 'no-habit' | 'unsupported' {
  const habit = useHabitStore.getState().habits.find((h) => h.id === habitId);
  if (!habit || habit.archivedAt) return 'no-habit';
  // Hydration habits complete from the water tracker; labelled-slot habits (the
  // prayers) complete per named slot — neither has one "done" a practice can own.
  // Nutrition-linked habits (phase 3b-2) ask what you ate on every tick — same reason; so does
  // the supplements habit (2026-10-23: which doses were taken).
  const type = habit.disciplineAction?.type;
  if (type === 'hydration' || type === 'nutrition' || type === 'supplements' || isLabeledHabit(habit)) return 'unsupported';
  return 'ok';
}

export const useCarePracticeStore = create<CarePracticeStore>((set, get) => {
  /**
   * ⚠️ Merged into the cloud's copy on every write (lib/preferenceSync.ts), per
   * practice — a stale tab can no longer put back a practice deleted elsewhere
   * or undo a link closed there.
   */
  const practiceSync = createPreferenceSync<CarePractice[]>({
    column: 'care_practices',
    rule: { records: 'id' },
    decode: parsePractices,
    read: () => get().practices,
    adopt: (practices) => set({ practices }),
    label: 'care practices',
    loadedFor: () => get().initializedUserId,
  });

  const writePractices = (practices: CarePractice[]) => {
    practiceSync.recordBase(get().practices);
    set({ practices });
    practiceSync.schedule();
  };

  /*
   * ⚠️⚠️ COLUMN BY COLUMN, NEVER THE ROW (docs/DAY_LOG_STALE_WRITES.md). A day's row has
   * independent columns — `done`, `helpful`, `habit_id` — and every writer used to send
   * all of them from this tab's copy: a stale "Not helpful" re-created a completion
   * undone elsewhere (`done: true`, and its XP), or wrote `done: false` over one made
   * there. Each writer now sends ONLY the column it changes; an upsert of a subset
   * updates only those columns on conflict, so no read or compare-and-swap is needed.
   * ⚠️ `done` DEFAULTS TO TRUE in the table, so a new row is never created without an
   * explicit `done` (see `setHelpfulCol`).
   */

  /** Mark days done — `done` (+ where it came from) only; `helpful` is never sent. The saved rows, or null. */
  const markDoneRows = async (practiceId: string, days: string[], habitId?: string): Promise<CareLogRow[] | null> => {
    if (days.length === 0) return [];
    const { user } = await syncManager.safeAuthCheck();
    if (!user) return null;
    const stamp = new Date().toISOString();
    const { data, error } = await supabase
      .from('care_practice_log')
      .upsert(
        days.map((day) => ({ user_id: user.id, practice_id: practiceId, day, done: true, ...(habitId ? { habit_id: habitId } : {}), updated_at: stamp })),
        { onConflict: 'user_id,practice_id,day' },
      )
      .select(LOG_COLUMNS);
    if (error) {
      console.error('Error saving care practice log:', error);
      return null;
    }
    return ((data ?? []) as LogRowDb[]).map(fromDb);
  };

  /** Change one column of an existing day; the row after it — null when there is no row. `false` = failed. */
  const updateCol = async (practiceId: string, day: string, cols: Partial<Pick<LogRowDb, 'done' | 'helpful'>>): Promise<CareLogRow | null | false> => {
    const { user } = await syncManager.safeAuthCheck();
    if (!user) return false;
    const { data, error } = await supabase
      .from('care_practice_log')
      .update({ ...cols, updated_at: new Date().toISOString() })
      .eq('user_id', user.id).eq('practice_id', practiceId).eq('day', day)
      .select(LOG_COLUMNS);
    if (error) {
      console.error('Error saving care practice log:', error);
      return false;
    }
    const row = ((data ?? []) as LogRowDb[])[0];
    return row ? fromDb(row) : null;
  };

  /**
   * Set "helpful" on a day: update it on the row; no row → create one NOT done (the
   * column default is done). Another device creating the row meanwhile (23505) → update.
   */
  const setHelpfulCol = async (practiceId: string, day: string, helpful: boolean, at: number): Promise<CareLogRow | false> => {
    const updated = await updateCol(practiceId, day, { helpful });
    if (updated !== null) return updated;
    // ⚠️ Signed out meanwhile: the insert below asks who is signed in NOW — it would
    // create this practice's day for the next account.
    if (at !== epoch) return false;
    const { user } = await syncManager.safeAuthCheck();
    if (!user) return false;
    const { data, error } = await supabase
      .from('care_practice_log')
      .insert({ user_id: user.id, practice_id: practiceId, day, done: false, helpful, updated_at: new Date().toISOString() })
      .select(LOG_COLUMNS);
    if (error) {
      if ((error as { code?: string }).code === '23505') return (await updateCol(practiceId, day, { helpful })) || false;
      console.error('Error saving care practice log:', error);
      return false;
    }
    const row = ((data ?? []) as LogRowDb[])[0];
    return row ? fromDb(row) : false;
  };

  /** A day that now says nothing (not done, no "helpful") is removed. Its own failure only leaves an empty row. */
  const dropIfEmpty = async (practiceId: string, day: string): Promise<void> => {
    const { user } = await syncManager.safeAuthCheck();
    if (!user) return;
    const { error } = await supabase
      .from('care_practice_log')
      .delete()
      .eq('user_id', user.id).eq('practice_id', practiceId).eq('day', day)
      .eq('done', false).is('helpful', null);
    if (error) console.error('Error deleting care practice day:', error);
  };

  /** Put the server's answer for a day on screen (null = no row). */
  const showDay = (key: string, row: CareLogRow | null) => {
    set((s) => {
      const log = { ...s.log };
      if (row && (row.done || row.helpful !== null)) log[key] = row; else delete log[key];
      return { log, error: null };
    });
  };

  /**
   * Close a practice's open link on `day`: copy the days its habit was done into
   * the log (with the habit's id), THEN mark the link closed. ⚠️ The order is the
   * guard — if the copy fails the link stays open, so its history is never lost.
   * ⚠️ The habit is read SYNCHRONOUSLY, before any await: on delete it is about
   * to disappear.
   */
  const closeLink = async (practiceId: string, day: string): Promise<boolean> => {
    const practice = get().practices.find((p) => p.id === practiceId);
    const link = practice && openLink(practice);
    if (!practice || !link) return true;
    const habit = useHabitStore.getState().habits.find((h) => h.id === link.habitId);
    const { log } = get();
    // `done` + the habit's id only — a "helpful" set on any of these days (here or on
    // another device) is left as the row has it.
    const days = linkedDoneDays(link, habit, day).filter((d) => !log[logKey(practiceId, d)]?.done);
    const at = epoch;
    const rows = await markDoneRows(practiceId, days, link.habitId);
    // Signed out while it saved (for this account): the link is the next account's to
    // leave alone — nothing lands, nothing is rewritten.
    if (at !== epoch) return false;
    if (!rows) {
      set({ error: 'Could not save this practice’s history. The habit link was kept — try again.' });
      return false;
    }
    set((s) => ({ log: { ...s.log, ...Object.fromEntries(rows.map((r) => [logKey(r.practiceId, r.day), r])) } }));
    writePractices(get().practices.map((p) => (p.id !== practiceId ? p : {
      ...p,
      links: p.links.map((l) => (l.to === null ? { ...l, to: day } : l)),
    })));
    return true;
  };

  return {
    practices: [],
    log: {},
    ready: false,
    loading: false,
    initializedUserId: null,
    error: null,

    initialize: async () => {
      if (get().loading) return;
      const at = epoch;
      const { user } = await syncManager.safeAuthCheck();
      // Signed out while it asked: never mark the next account's store "loading" for this one.
      if (!user || at !== epoch) return;
      if (get().initializedUserId === user.id) return;
      // Another account's practices must never show, or be written, while this one loads.
      practiceSync.reset();
      set({ loading: true, ready: false, practices: [], log: {} });
      try {
        const prefs = await fetchUserPreferences();
        if (!prefs || prefs.userId !== user.id) return;
        const since = shiftYMD(today(), -CARE_HISTORY_DAYS);
        const { data, error } = await supabase
          .from('care_practice_log')
          .select('practice_id, day, done, helpful, habit_id')
          .eq('user_id', user.id)
          .gte('day', since);
        // Signed out during either read: neither this account's practices nor its days land.
        if (at !== epoch) return;
        if (error) throw error;
        const log: Record<string, CareLogRow> = {};
        for (const r of (data ?? []) as LogRowDb[]) {
          log[logKey(r.practice_id, r.day)] = {
            practiceId: r.practice_id, day: r.day, done: r.done, helpful: r.helpful, habitId: r.habit_id,
          };
        }
        set({ practices: practiceSync.load(prefs.row?.care_practices ?? null, prefs.row?.updated_at), log, ready: true, initializedUserId: user.id });
      } catch (err) {
        console.error('Error initializing care practices:', err);
      } finally {
        if (at === epoch) set({ loading: false }); // else the next account's load may be running
      }
    },

    addPractice: ({ name, instructions = '', starterKey }) => {
      if (!get().ready) return null;
      const id = `care-${crypto.randomUUID()}`;
      writePractices([...get().practices, {
        id, name: name.trim() || 'My practice', instructions: instructions.trim(), starterKey,
        links: [], createdAt: new Date().toISOString(), archivedAt: null,
      }]);
      return id;
    },

    updatePractice: (id, patch) => {
      if (!get().ready) return;
      writePractices(get().practices.map((p) => (p.id !== id ? p : {
        ...p,
        ...(patch.name !== undefined ? { name: patch.name.trim() || p.name } : {}),
        ...(patch.instructions !== undefined ? { instructions: patch.instructions.trim() } : {}),
      })));
    },

    // ⚠️ Archiving keeps the practice, its log and its XP; it only hides it. An
    // open link is left as it is, so restoring picks up where it was.
    archivePractice: (id) => {
      if (!get().ready) return;
      writePractices(get().practices.map((p) => (p.id === id ? { ...p, archivedAt: new Date().toISOString() } : p)));
    },

    restorePractice: (id) => {
      if (!get().ready) return;
      const p = get().practices.find((x) => x.id === id);
      const link = p && openLink(p);
      // One practice per habit: if another practice took the habit meanwhile,
      // this one comes back unlinked rather than silently sharing it.
      const taken = link && practiceLinkedTo(get().practices, link.habitId);
      writePractices(get().practices.map((x) => (x.id !== id ? x : {
        ...x,
        archivedAt: null,
        links: taken ? x.links.map((l) => (l.to === null ? { ...l, to: today() } : l)) : x.links,
      })));
    },

    linkHabit: async (practiceId, habitId) => {
      if (!get().ready) return 'not-ready';
      const practice = get().practices.find((p) => p.id === practiceId);
      if (!practice) return 'no-practice';
      const ok = linkable(habitId);
      if (ok !== 'ok') return ok;
      const other = practiceLinkedTo(get().practices, habitId);
      if (other && other.id !== practiceId) return 'habit-taken';
      if (openLink(practice)?.habitId === habitId) return 'ok';

      const day = today();
      // Close the current link first (history copied), then open the new one.
      if (!(await closeLink(practiceId, day))) return 'save-failed';
      writePractices(get().practices.map((p) => (p.id !== practiceId ? p : {
        ...p, links: [...p.links, { habitId, from: day, to: null }],
      })));
      set({ error: null });
      return 'ok';
    },

    unlinkHabit: async (practiceId) => {
      if (!get().ready) return false;
      const okay = await closeLink(practiceId, today());
      if (okay) set({ error: null });
      return okay;
    },

    complete: async (practiceId, day) => {
      if (!get().ready || day > today()) return;
      const practice = get().practices.find((p) => p.id === practiceId);
      if (!practice || practice.archivedAt) return;
      if (get().isDone(practiceId, day)) return; // idempotent

      const link = openLink(practice);
      if (link && day >= link.from) {
        // ⭐ Through the habit — the habit IS the record. A count habit takes one
        // step toward its target; the practice reads done once the habit is.
        const habits = useHabitStore.getState();
        const habit = habits.habits.find((h) => h.id === link.habitId);
        if (!habit) return;
        if (habitTarget(habit) > 1) habits.incrementHabit(habit.id, day);
        else habits.toggleHabit(habit.id, day);
        return;
      }

      const prev = get().log[logKey(practiceId, day)];
      const row: CareLogRow = { practiceId, day, done: true, helpful: prev?.helpful ?? null, habitId: prev?.habitId ?? null };
      set((s) => ({ log: { ...s.log, [logKey(practiceId, day)]: row } }));
      const at = epoch;
      const saved = await markDoneRows(practiceId, [day]);
      if (at !== epoch) return; // signed out while it saved: neither the day nor a rollback lands
      if (saved) {
        showDay(logKey(practiceId, day), saved[0] ?? row);
      } else {
        // ⚠️ Roll back: the page must never show a completion that was not saved.
        set((s) => {
          const log = { ...s.log };
          if (prev) log[logKey(practiceId, day)] = prev; else delete log[logKey(practiceId, day)];
          return { log, error: 'Could not save that completion. Try again.' };
        });
      }
    },

    undo: async (practiceId, day) => {
      if (!get().ready) return;
      const practice = get().practices.find((p) => p.id === practiceId);
      if (!practice) return;

      const link = openLink(practice);
      const habits = useHabitStore.getState();
      const habit = link && day >= link.from ? habits.habits.find((h) => h.id === link.habitId) : undefined;
      if (habit && habit.completedDates.includes(day)) {
        if (habitTarget(habit) > 1) habits.decrementHabit(habit.id, day);
        else habits.toggleHabit(habit.id, day);
        return;
      }

      const key = logKey(practiceId, day);
      const prev = get().log[key];
      if (!prev?.done) return;
      // A "Not helpful today" on the day survives the undo; otherwise the row goes.
      const keep = prev.helpful !== null;
      set((s) => {
        const log = { ...s.log };
        if (keep) log[key] = { ...prev, done: false }; else delete log[key];
        return { log };
      });
      // `done` only, then drop the row if it now says nothing — so a "helpful" set on
      // another device survives (this tab's copy may not have it).
      const at = epoch;
      const saved = await updateCol(practiceId, day, { done: false });
      if (at !== epoch) return; // signed out while it saved: neither the day nor a rollback lands
      if (saved === false) {
        set((s) => ({ log: { ...s.log, [key]: prev }, error: 'Could not undo that. Try again.' }));
        return;
      }
      if (saved && saved.helpful === null) await dropIfEmpty(practiceId, day);
      showDay(key, saved);
    },

    /**
     * "Not helpful today" (false), "helpful" (true) or cleared (null).
     * ⚠️ Never reads or changes completion, and never feeds XP.
     */
    setHelpful: async (practiceId, day, helpful) => {
      if (!get().ready) return;
      const key = logKey(practiceId, day);
      const prev = get().log[key];
      if (!prev && helpful === null) return;
      const next: CareLogRow = prev ? { ...prev, helpful } : { practiceId, day, done: false, helpful, habitId: null };
      const drop = !next.done && next.helpful === null;
      set((s) => {
        const log = { ...s.log };
        if (drop) delete log[key]; else log[key] = next;
        return { log };
      });
      // ⚠️ `helpful` ONLY — this tab's `done` may be stale (undone, or completed, on
      // another device), and writing it back would re-create or erase a completion.
      const at = epoch;
      let saved: CareLogRow | null | false;
      if (helpful === null) {
        saved = await updateCol(practiceId, day, { helpful: null });
        if (saved && !saved.done) await dropIfEmpty(practiceId, day);
      } else {
        saved = await setHelpfulCol(practiceId, day, helpful, at);
      }
      if (at !== epoch) return; // signed out while it saved: neither the day nor a rollback lands
      if (saved !== false) {
        showDay(key, saved);
      } else {
        set((s) => {
          const log = { ...s.log };
          if (prev) log[key] = prev; else delete log[key];
          return { log, error: 'Could not save that. Try again.' };
        });
      }
    },

    onHabitDeleted: async (habitId) => {
      const linked = get().practices.filter((p) => openLink(p)?.habitId === habitId);
      await Promise.all(linked.map((p) => closeLink(p.id, today())));
    },

    isDone: (practiceId, day) => {
      const practice = get().practices.find((p) => p.id === practiceId);
      if (!practice) return false;
      return isDoneOn(practice, day, get().log, useHabitStore.getState().habits);
    },

    clearError: () => set({ error: null }),

    /**
     * Back to empty — run on sign-out (lib/signOutReset). `initialize` already swaps the
     * practices when ANOTHER account loads, but until then (or if that load fails) the last
     * account's practices and days stayed on screen, `ready` to be written. The sync's base
     * goes too, so a write still in flight never brings them back.
     */
    clearStore: () => {
      epoch += 1;
      practiceSync.reset();
      set({ practices: [], log: {}, ready: false, loading: false, initializedUserId: null, error: null });
    },
  };
});

onSignOut(() => useCarePracticeStore.getState().clearStore());
